// ============================================================
// src/drive.js — Integración con Google Drive API
// ============================================================

import { logInfo, logWarn } from './core/logger.js';
import { manejarError } from './core/errors.js';
import { getRuntimeFlags } from './runtime-flags.js';

// ============================================================
// Token en memoria + localStorage con TTL
// ============================================================

const LS_TOKEN_KEY  = 'drive_access_token';
const LS_EXPIRY_KEY = 'drive_token_expiry';
// Access tokens duran 3600s; usamos 55 min para renovar antes de que expiren
const TOKEN_TTL_MS  = 55 * 60 * 1000;

let accessTokenDrive = null;

// Restaurar token persistido si aún no expiró
(function _restaurarToken() {
    const token  = localStorage.getItem(LS_TOKEN_KEY);
    const expiry = Number(localStorage.getItem(LS_EXPIRY_KEY) || 0);
    if (token && Date.now() < expiry) {
        accessTokenDrive = token;
        logInfo('Drive', 'Token restaurado desde localStorage');
    } else if (token) {
        localStorage.removeItem(LS_TOKEN_KEY);
        localStorage.removeItem(LS_EXPIRY_KEY);
    }
}());

// ============================================================
// RENOVADOR DE TOKEN (inyectado desde auth.js para evitar ciclo)
// ============================================================

let _renovarTokenFn = null;

export function registrarRenovadorToken(fn) {
    _renovarTokenFn = fn;
}

// ============================================================
// TOKEN
// ============================================================

export function guardarAccessToken(token) {
    accessTokenDrive = token;
    localStorage.setItem(LS_TOKEN_KEY,  token);
    localStorage.setItem(LS_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
    logInfo('Drive', 'Access token guardado');
}

export function tienePermisosDrive() {
    return accessTokenDrive !== null;
}

/** true si hay token en memoria/localStorage y aún no expiró */
export function tokenDriveEsValido() {
    if (!accessTokenDrive) return false;
    const expiry = Number(localStorage.getItem(LS_EXPIRY_KEY) || 0);
    return Date.now() < expiry;
}

export function obtenerAccessToken() {
    return accessTokenDrive;
}

export function limpiarAccessToken() {
    accessTokenDrive = null;
    localStorage.removeItem(LS_TOKEN_KEY);
    localStorage.removeItem(LS_EXPIRY_KEY);
    logInfo('Drive', 'Access token limpiado');
}

// ============================================================
// CONSTANTES API
// ============================================================

const URL_DRIVE_API = 'https://www.googleapis.com/drive/v3';
const URL_UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';

// ============================================================
// HEADERS
// ============================================================

function cabecerasApi() {
  return {
    Authorization: `Bearer ${accessTokenDrive}`,
    'Content-Type': 'application/json',
  };
}

// ============================================================
// HELPERS DE ERROR
// ============================================================

function crearErrorDrive(code, message, extra = {}) {
  const error = new Error(message);
  error.code = code;
  Object.assign(error, extra);
  return error;
}

function extraerMensajeErrorDrive(payload = {}) {
  return String(
    payload?.error?.message ||
    payload?.error_description ||
    ''
  ).trim();
}

function clasificarErrorDrive(status, payload = {}) {
  const mensaje = extraerMensajeErrorDrive(payload).toLowerCase();

  if (status === 401) {
    return crearErrorDrive(
      'drive/session-expired',
      'Tu sesión de Google Drive expiró. Vuelve a autorizar para continuar.'
    );
  }

  if (status === 403) {
    if (
      mensaje.includes('insufficient') ||
      mensaje.includes('permission') ||
      mensaje.includes('scope')
    ) {
      return crearErrorDrive(
        'drive/permission-denied',
        'Faltan permisos de Google Drive para completar esta acción.'
      );
    }

    if (
      mensaje.includes('revoked') ||
      mensaje.includes('invalid grant') ||
      mensaje.includes('expired or revoked')
    ) {
      return crearErrorDrive(
        'drive/access-revoked',
        'El acceso a Google Drive fue revocado. Debes autorizar nuevamente.'
      );
    }

    return crearErrorDrive(
      'drive/permission-denied',
      'Google Drive rechazó la operación por permisos insuficientes.'
    );
  }

  return crearErrorDrive(
    'drive/api-error',
    extraerMensajeErrorDrive(payload) || `Error HTTP ${status}`,
    { status }
  );
}

export function requiereReautorizacionDrive(error) {
  const code = String(error?.code || '').toLowerCase();

  return (
    code === 'drive/not-authorized' ||
    code === 'drive/session-expired' ||
    code === 'drive/access-revoked' ||
    code === 'drive/permission-denied'
  );
}

// ============================================================
// LLAMADAS API
// ============================================================

async function llamarApi(url, opciones = {}, _retry = true) {
  try {
    if (!accessTokenDrive) {
      throw crearErrorDrive(
        'drive/not-authorized',
        'Google Drive no está autorizado en esta sesión.'
      );
    }

    const respuesta = await fetch(url, {
      ...opciones,
      headers: { ...cabecerasApi(), ...(opciones.headers ?? {}) },
    });

    if (!respuesta.ok) {
      const payload = await respuesta.json().catch(() => ({}));

      if (respuesta.status === 401) {
        logWarn('Drive', 'Token expirado (401), intentando renovar...');
        limpiarAccessToken();

        if (_retry && _renovarTokenFn) {
          const renovado = await _renovarTokenFn();
          if (renovado) {
            return llamarApi(url, opciones, false);
          }
        }
      } else if (respuesta.status === 403) {
        logWarn('Drive', `Error de autorización (403)`, payload);
        limpiarAccessToken();
      }

      throw clasificarErrorDrive(respuesta.status, payload);
    }

    if (respuesta.status === 204) {
      return null;
    }

    return await respuesta.json();

  } catch (error) {
    manejarError(error, 'Drive API');
    throw error;
  }
}

// ============================================================
// HELPERS
// ============================================================

function normalizarNombre(valor) {
  return String(valor ?? '').replace(/\s+/g, ' ').trim();
}

function escaparValorQueryDrive(valor) {
  return normalizarNombre(valor)
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'");
}

function _normalizarListaIds(lista) {
  return Array.from(
    new Set(
      (Array.isArray(lista) ? lista : [])
        .map(v => String(v || '').trim())
        .filter(Boolean)
    )
  );
}

// ============================================================
// CARPETAS
// ============================================================

async function buscarCarpeta(nombre, idPadre = null) {
  const nombreNormalizado = normalizarNombre(nombre);
  if (!nombreNormalizado) return null;

  const nombreQuery = escaparValorQueryDrive(nombreNormalizado);

  const padreQuery = idPadre
    ? ` and '${idPadre}' in parents`
    : ` and 'root' in parents`;

  const query = encodeURIComponent(
    `mimeType='application/vnd.google-apps.folder'`
    + ` and name='${nombreQuery}'`
    + ` and trashed=false`
    + padreQuery
  );

  const datos = await llamarApi(
    `${URL_DRIVE_API}/files?q=${query}&fields=files(id,name)&spaces=drive`
  );

  return datos?.files?.[0]?.id ?? null;
}

async function crearCarpeta(nombre, idPadre = null) {

  const nombreNormalizado = normalizarNombre(nombre);

  const metadatos = {
    name: nombreNormalizado,
    mimeType: 'application/vnd.google-apps.folder',
    ...(idPadre ? { parents: [idPadre] } : {}),
  };

  const datos = await llamarApi(`${URL_DRIVE_API}/files`, {
    method: 'POST',
    body: JSON.stringify(metadatos),
  });

  logInfo('Drive', `Carpeta creada: ${nombreNormalizado}`, datos.id);

  return datos.id;
}

async function obtenerOCrearCarpeta(nombre, idPadre = null) {

  const idExistente = await buscarCarpeta(nombre, idPadre);

  if (idExistente) {
    logInfo('Drive', 'Carpeta existente reutilizada', idExistente);
    return idExistente;
  }

  return crearCarpeta(nombre, idPadre);
}

// ============================================================
// ESTRUCTURA DE CARPETAS
// ============================================================

const NOMBRE_RAIZ = 'Gestor de Concursos';

const CARPETAS_TIPO = {
  log: '01 - Log',
  conf: '02 - Confirmaciones',
  res: '03 - Resultados',
  cert: '04 - Certificados',
  reg: '05 - Reglas',
};

export async function obtenerCarpetaDestino(edicion, tipo) {

  const idRaiz = await obtenerOCrearCarpeta(NOMBRE_RAIZ);

  const indicativo = normalizarNombre(
    edicion.indicativo ?? edicion.indicativoOperador ?? 'SIN-INDICATIVO'
  );

  const idIndicativo = await obtenerOCrearCarpeta(indicativo, idRaiz);

  const nombreConcurso = normalizarNombre(edicion.concurso ?? 'SIN-CONCURSO');
  const idConcurso = await obtenerOCrearCarpeta(nombreConcurso, idIndicativo);

  const año = normalizarNombre(String(edicion.año ?? 'SIN-AÑO'));
  const idAño = await obtenerOCrearCarpeta(año, idConcurso);

  const modo = normalizarNombre(edicion.modo ?? 'SIN-MODO');
  const idModo = await obtenerOCrearCarpeta(modo, idAño);

  const nombreCarpeta = CARPETAS_TIPO[tipo] ?? tipo;

  return obtenerOCrearCarpeta(nombreCarpeta, idModo);
}

export async function crearEstructuraCarpetas(edicion) {

  const idRaiz = await obtenerOCrearCarpeta(NOMBRE_RAIZ);

  const indicativo = normalizarNombre(edicion.indicativo ?? edicion.indicativoOperador);
  const concurso = normalizarNombre(edicion.concurso);
  const año = normalizarNombre(String(edicion.año));
  const modo = normalizarNombre(edicion.modo);

  const idIndicativo = await obtenerOCrearCarpeta(indicativo, idRaiz);
  const idConcurso = await obtenerOCrearCarpeta(concurso, idIndicativo);
  const idAño = await obtenerOCrearCarpeta(año, idConcurso);
  const idModo = await obtenerOCrearCarpeta(modo, idAño);

  const [log, conf, res, cert, reg] = await Promise.all([
    obtenerOCrearCarpeta(CARPETAS_TIPO.log, idModo),
    obtenerOCrearCarpeta(CARPETAS_TIPO.conf, idModo),
    obtenerOCrearCarpeta(CARPETAS_TIPO.res, idModo),
    obtenerOCrearCarpeta(CARPETAS_TIPO.cert, idModo),
    obtenerOCrearCarpeta(CARPETAS_TIPO.reg, idModo),
  ]);

  logInfo('Drive', `Estructura creada para ${concurso} ${año}`);

  return {
    raiz: idRaiz,
    indicativo: idIndicativo,
    concurso: idConcurso,
    año: idAño,
    modo: idModo,
    log,
    conf,
    res,
    cert,
    reg,
  };
}

// ============================================================
// SUBIDA DE ARCHIVOS
// ============================================================

export async function subirArchivo(archivo, idCarpeta, _retry = true) {

  try {

    if (!accessTokenDrive) {
      throw crearErrorDrive(
        'drive/not-authorized',
        'Google Drive no está autorizado en esta sesión.'
      );
    }

    const _flags = await getRuntimeFlags();
    if (_flags.emergencyMode || !_flags.uploadsEnabled) {
      throw crearErrorDrive(
        'drive/uploads-disabled',
        'Las subidas están temporalmente deshabilitadas.'
      );
    }

    const metadatos = {
      name: archivo.name,
      parents: [idCarpeta],
    };

    const boundary = '-------314159265358979323846';
    const delimitador = `\r\n--${boundary}\r\n`;
    const cierre = `\r\n--${boundary}--`;

    const contenidoArchivo = await archivo.arrayBuffer();

    const encoder = new TextEncoder();

    const partMeta = encoder.encode(
      delimitador +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadatos)
    );

    const partFileHeader = encoder.encode(
      `\r\n--${boundary}\r\nContent-Type: ${archivo.type || 'application/octet-stream'}\r\n\r\n`
    );

    const contenido = new Uint8Array(contenidoArchivo);
    const end = encoder.encode(cierre);

    const body = new Uint8Array(
      partMeta.length + partFileHeader.length + contenido.length + end.length
    );

    body.set(partMeta, 0);
    body.set(partFileHeader, partMeta.length);
    body.set(contenido, partMeta.length + partFileHeader.length);
    body.set(end, partMeta.length + partFileHeader.length + contenido.length);

    const respuesta = await fetch(
      `${URL_UPLOAD_API}/files?uploadType=multipart&fields=id,name,webViewLink,size,mimeType`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessTokenDrive}`,
          'Content-Type': `multipart/related; boundary="${boundary}"`,
        },
        body,
      }
    );

    if (!respuesta.ok) {
      const payload = await respuesta.json().catch(() => ({}));

      if (respuesta.status === 401) {
        logWarn('Drive', 'Token expirado en subirArchivo (401), intentando renovar...');
        limpiarAccessToken();

        if (_retry && _renovarTokenFn) {
          const renovado = await _renovarTokenFn();
          if (renovado) {
            return subirArchivo(archivo, idCarpeta, false);
          }
        }
      } else if (respuesta.status === 403) {
        limpiarAccessToken();
      }

      throw clasificarErrorDrive(respuesta.status, payload);
    }

    const datos = await respuesta.json();

    logInfo('Drive', 'Archivo subido', datos);

    return datos;

  } catch (error) {

    manejarError(error, 'Drive subirArchivo');
    throw error;
  }
}

// ============================================================
// DESCARGAR CONTENIDO (para archivos que la app necesita leer
// del lado del cliente, p. ej. un log Cabrillo ya subido)
// ============================================================

export async function descargarContenidoArchivo(fileId, _retry = true) {
  try {
    if (!accessTokenDrive) {
      throw crearErrorDrive(
        'drive/not-authorized',
        'Google Drive no está autorizado en esta sesión.'
      );
    }

    const respuesta = await fetch(`${URL_DRIVE_API}/files/${fileId}?alt=media`, {
      headers: { Authorization: `Bearer ${accessTokenDrive}` },
    });

    if (!respuesta.ok) {
      const payload = await respuesta.json().catch(() => ({}));

      if (respuesta.status === 401) {
        logWarn('Drive', 'Token expirado en descargarContenidoArchivo (401), intentando renovar...');
        limpiarAccessToken();

        if (_retry && _renovarTokenFn) {
          const renovado = await _renovarTokenFn();
          if (renovado) {
            return descargarContenidoArchivo(fileId, false);
          }
        }
      } else if (respuesta.status === 403) {
        limpiarAccessToken();
      }

      throw clasificarErrorDrive(respuesta.status, payload);
    }

    return await respuesta.arrayBuffer();

  } catch (error) {

    manejarError(error, 'Drive descargarContenidoArchivo');
    throw error;
  }
}

// ============================================================
// ELIMINAR ARCHIVO / CARPETA
// ============================================================

export async function eliminarArchivoEnDrive(driveId) {
  return eliminarRecursoEnDrive(driveId);
}

async function eliminarRecursoEnDrive(driveId) {

  try {

    if (!accessTokenDrive) {
      throw crearErrorDrive(
        'drive/not-authorized',
        'Google Drive no está autorizado en esta sesión.'
      );
    }

    const respuesta = await fetch(`${URL_DRIVE_API}/files/${driveId}`, {
      method: 'DELETE',
      headers: cabecerasApi(),
    });

    if (respuesta.status === 404) {
      logInfo('Drive', 'Recurso ya no existe, se considera eliminado', driveId);
      return true;
    }

    if (respuesta.status === 401 || respuesta.status === 403) {
      let payload = {};
      try {
        payload = await respuesta.json();
      } catch {
        payload = {};
      }

      limpiarAccessToken();
      throw clasificarErrorDrive(respuesta.status, payload);
    }

    if (respuesta.status === 204 || respuesta.ok) {
      logInfo('Drive', 'Recurso eliminado', driveId);
      return true;
    }

    throw crearErrorDrive('drive/delete-error', `Error eliminando recurso ${driveId}`);

  } catch (error) {

    manejarError(error, 'Drive eliminarRecurso');
    throw error;
  }
}

// ============================================================
// ELIMINACIÓN COMPLETA DE RECURSOS DE DRIVE DE LA APP
// ============================================================

export async function eliminarRecursosDriveCuenta(recursos = {}, onProgreso = () => {}) {

  const archivoIds = _normalizarListaIds(recursos.archivoIds);
  const folderIds = _normalizarListaIds(recursos.folderIds);

  const resultado = {
    omitido: false,
    razon: '',
    totalArchivos: archivoIds.length,
    totalCarpetas: folderIds.length,
    eliminadosArchivos: 0,
    eliminadasCarpetas: 0,
    errores: []
  };

  if (!accessTokenDrive) {
    resultado.omitido = true;
    resultado.razon = 'sin_autorizacion';
    return resultado;
  }

  const total = archivoIds.length + folderIds.length;

  if (total === 0) {
    onProgreso(100, 'No había recursos de Drive para eliminar.');
    return resultado;
  }

  let procesados = 0;

  onProgreso(5, 'Eliminando archivos de Drive creados por la app...');

  for (const driveId of archivoIds) {
    try {
      await eliminarRecursoEnDrive(driveId);
      resultado.eliminadosArchivos += 1;
    } catch (error) {
      resultado.errores.push({
        tipo: 'archivo',
        id: driveId,
        mensaje: error.message
      });

      if (requiereReautorizacionDrive(error)) {
        resultado.omitido = true;
        resultado.razon = String(error.code || 'drive/session-expired');
        return resultado;
      }
    }

    procesados += 1;
    onProgreso(
      Math.round((procesados / total) * 100),
      `Eliminando recursos de Drive... (${procesados}/${total})`
    );
  }

  onProgreso(
    Math.round((procesados / total) * 100),
    'Eliminando carpetas de Drive creadas por la app...'
  );

  for (const driveId of folderIds) {
    try {
      await eliminarRecursoEnDrive(driveId);
      resultado.eliminadasCarpetas += 1;
    } catch (error) {
      resultado.errores.push({
        tipo: 'carpeta',
        id: driveId,
        mensaje: error.message
      });

      if (requiereReautorizacionDrive(error)) {
        resultado.omitido = true;
        resultado.razon = String(error.code || 'drive/session-expired');
        return resultado;
      }
    }

    procesados += 1;
    onProgreso(
      Math.round((procesados / total) * 100),
      `Eliminando recursos de Drive... (${procesados}/${total})`
    );
  }

  onProgreso(100, 'Recursos de Drive procesados.');

  return resultado;
}

